import { ProductionState } from "config";
import { US_STATES } from "config/states";
import type { BudgetAssumptions } from "components/dashboard/body/projects/panels/budget/BudgetAssumptionsTypes";
import type { BudgetI } from "components/dashboard/body/projects/panels/budget/types";

export const STRIPBOARD_PRODUCTION_BASE_LABEL = "Scene locations from stripboard";

export interface ProductionLocationValue {
  name: string;
  place_id?: string | null;
}

export interface BudgetDisplayLocationInput {
  rerunParams?: BudgetAssumptions | null;
  assumptions?: BudgetI["assumptions"];
  versionProductionState?: string | null;
  versionProductionLocation?: ProductionLocationValue | null;
  projectProductionState?: string | null;
}

export interface BudgetDisplayLocationResult {
  productionBaseLabel: string | null;
  taxState: ProductionState | null;
  usesStripboardLocations: boolean;
}

function normalizeProductionStateLabel(raw: string | null | undefined): ProductionState | null {
  if (!raw?.trim()) return null;
  const trimmed = raw.trim();
  if (Object.values(ProductionState).includes(trimmed as ProductionState)) {
    return trimmed as ProductionState;
  }
  const byCode = US_STATES.find((state) => state.value.toUpperCase() === trimmed.toUpperCase());
  if (byCode) {
    return byCode.label as ProductionState;
  }
  const byLabel = US_STATES.find((state) => state.label.toLowerCase() === trimmed.toLowerCase());
  return byLabel ? (byLabel.label as ProductionState) : null;
}

function findAssumptionLocationName(
  assumptions: BudgetI["assumptions"] | undefined
): string | null {
  if (!assumptions?.length) return null;
  const match = assumptions.find((entry) => entry.name?.toUpperCase() === "LOCATION");
  const value = match?.value?.trim();
  return value && value !== "undefined" ? value : null;
}

export function resolveBudgetDisplayLocation(
  input: BudgetDisplayLocationInput
): BudgetDisplayLocationResult {
  const rerunParams = input.rerunParams;

  if (rerunParams?.use_stripboard_locations) {
    return {
      productionBaseLabel: STRIPBOARD_PRODUCTION_BASE_LABEL,
      taxState: normalizeProductionStateLabel(
        input.versionProductionState ?? input.projectProductionState
      ),
      usesStripboardLocations: true,
    };
  }

  const rerunLocationName = rerunParams?.location?.name?.trim();
  if (rerunLocationName) {
    return {
      productionBaseLabel: rerunLocationName,
      taxState: normalizeProductionStateLabel(
        input.versionProductionState ?? input.projectProductionState
      ),
      usesStripboardLocations: false,
    };
  }

  const versionLocationName = input.versionProductionLocation?.name?.trim();
  if (versionLocationName) {
    return {
      productionBaseLabel: versionLocationName,
      taxState: normalizeProductionStateLabel(
        input.versionProductionState ?? input.projectProductionState
      ),
      usesStripboardLocations: false,
    };
  }

  const assumptionLocation = findAssumptionLocationName(input.assumptions);
  if (assumptionLocation) {
    return {
      productionBaseLabel: assumptionLocation,
      taxState: normalizeProductionStateLabel(
        input.versionProductionState ?? input.projectProductionState
      ),
      usesStripboardLocations: false,
    };
  }

  const taxState = normalizeProductionStateLabel(
    input.versionProductionState ?? input.projectProductionState
  );
  if (taxState) {
    return {
      productionBaseLabel: taxState,
      taxState,
      usesStripboardLocations: false,
    };
  }

  return {
    productionBaseLabel: null,
    taxState: null,
    usesStripboardLocations: false,
  };
}
